import { defineMessages, FormattedMessage } from '@faceup/localization'
import { Alert, Divider, Icon, Modal, Space, Typography } from '@faceup/ui-base'
import { type ReactNode, useContext, useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import FaceUpLogo from '../../assets/logo-fu.svg'
import { LayoutContext } from '../../Contexts/LayoutContext'
import { ReportFormContext } from '../../Contexts/ReportFormContext'
import { HideMedia, useMedia } from '../../mq'
import GreenShieldIcon from '../../Shared/assets/green-shield-icon.svg?react'

const messages = defineMessages('followUp.components.footer', [
  {
    id: 'systemSecured',
    defaultMessage: 'All communication is encrypted.',
    wrappers: { modal: true },
  },
  {
    id: 'systemSecuredAnonymous',
    defaultMessage:
      'All communication is anonymous and encrypted. <modal>Learn more about anonymity</modal>',
    wrappers: { modal: true },
  },
  { id: 'poweredBy', defaultMessage: 'System powered by' },
  { id: 'privacyPolicy', defaultMessage: 'Privacy Policy' },
  { id: 'anonymousMoreInfoTitle', defaultMessage: 'How to stay anonymous' },
  {
    id: 'anonymousMoreInfo0',
    defaultMessage:
      'Do not provide any personal information in the form unless you really want to.',
  },
  {
    id: 'anonymousMoreInfo1',
    defaultMessage: 'Do not submit a case using a work laptop, phone or other work device.',
  },
  {
    id: 'anonymousMoreInfo2',
    defaultMessage:
      "Don't submit a case while you're at work, on a work network or logged into a work account (for example, a Google account).",
  },
  {
    id: 'anonymousMoreInfo3',
    defaultMessage:
      'For added security when submitting a serious case, you can download and use the <link>Tor browser</link>.',
    wrappers: { link: true },
  },
] as const)

const fragments = {
  Footer_config: graphql(`
    fragment Footer_config on CompanyConfig {
      id
      isBrandingHidden
    }
  `),
}

type FooterProps = {
  config?: FragmentType<typeof fragments.Footer_config>
}

const Footer = ({ config: _config }: FooterProps) => {
  const config = getFragmentData(fragments.Footer_config, _config)
  const [anonymousInfoModalOpen, setAnonymousInfoModalOpen] = useState(false)
  const { client } = useContext(LayoutContext)
  const { isAnonymous } = useContext(ReportFormContext)

  const mdDown = useMedia('mdDown')

  const modalWrapper = (content: ReactNode) => (
    <button
      type='button'
      key='modal-link'
      className='text-colorSuccess underline font-semibold cursor-pointer bg-transparent border-0 p-0'
      onClick={() => setAnonymousInfoModalOpen(true)}
    >
      {content}
    </button>
  )

  return (
    <div className='flex flex-col'>
      <div className='mx-auto mb-2.5'>
        {/* #ENG-1657 for surveys we need to hide this - do it after https://github.com/faceup/monorepo/pull/4061 is merged*/}
        <Alert
          type='success'
          message={
            isAnonymous ? (
              <FormattedMessage
                message={messages.systemSecuredAnonymous}
                wrappers={{ modal: modalWrapper }}
              />
            ) : (
              <FormattedMessage
                message={messages.systemSecured}
                wrappers={{ modal: modalWrapper }}
              />
            )
          }
        />
        <Modal
          open={anonymousInfoModalOpen}
          title={<FormattedMessage message={messages.anonymousMoreInfoTitle} />}
          onClose={() => setAnonymousInfoModalOpen(false)}
        >
          <div className='flex items-center gap-3.5 mb-1.5'>
            <Icon component={GreenShieldIcon} style={{ fontSize: 20 }} />
            <FormattedMessage message={messages.anonymousMoreInfo0} />
          </div>
          <div className='flex items-center gap-3.5 mb-1.5'>
            <Icon component={GreenShieldIcon} style={{ fontSize: 20 }} />
            <FormattedMessage message={messages.anonymousMoreInfo1} />
          </div>
          <div className='flex items-center gap-3.5 mb-1.5'>
            <Icon component={GreenShieldIcon} style={{ fontSize: 20 }} />
            <FormattedMessage message={messages.anonymousMoreInfo2} />
          </div>
          <div className='flex items-center gap-3.5 mb-1.5'>
            <Icon component={GreenShieldIcon} style={{ fontSize: 20 }} />
            <div>
              <FormattedMessage
                message={messages.anonymousMoreInfo3}
                wrappers={{
                  link: content => (
                    <a href='https://www.torproject.org/' target='_blank' rel='noopener noreferrer'>
                      {content}
                    </a>
                  ),
                }}
              />
            </div>
          </div>
        </Modal>
      </div>

      <footer className='px-2.5 text-xs text-center bg-transparent py-6'>
        <Space align='center' size={mdDown ? 8 : 5} direction={mdDown ? 'vertical' : 'horizontal'}>
          <a
            href='https://www.faceup.com/en/privacy-policy'
            target='_blank'
            rel='noopener noreferrer'
          >
            <Typography.Text type='secondary' size='sm' underline>
              <FormattedMessage message={messages.privacyPolicy} />
            </Typography.Text>
          </a>

          {client !== 'app' && !config?.isBrandingHidden && (
            <>
              <HideMedia hideWhen='mdDown'>
                <Divider type='vertical' />
              </HideMedia>

              <Space>
                <Typography.Text type='secondary' size='sm'>
                  <FormattedMessage message={messages.poweredBy} />
                </Typography.Text>

                <a href='https://www.faceup.com/' target='_blank' rel='noopener noreferrer'>
                  <img className='max-h-5 w-auto' src={FaceUpLogo} alt='FaceUp' />
                </a>
              </Space>
            </>
          )}
        </Space>
      </footer>
    </div>
  )
}

export default Footer
